import React from 'react';
import PropTypes from 'prop-types';
import { Badge, BADGE_VARIANTS } from 'afl-design-system';

import { getTranslation } from 'common/react/utils/translations';

const NUMBERED_ROUND_ABBR = /^Rd\s/;

// Name, not abbreviation - 2025 was FW1/SF/PF/GF, 2026 is WF/QE/SF/PF/GF.
const FINALS_ROUND_NAME = /final/i;

// Finals go badge-only; their names would dwarf the cards and widen every gutter.
const getRoundLabel = (round) => {
    if (NUMBERED_ROUND_ABBR.test(round.abbreviation ?? '')) {
        return {
            label: getTranslation('label.fixtureBar.roundTitle'),
            badge: round.roundNumber ?? null
        };
    }

    if (round.name?.startsWith('Week')) {
        return {
            label: getTranslation('label.fixtureBar.weekTitle'),
            badge: round.roundNumber ?? null
        };
    }

    if (FINALS_ROUND_NAME.test(round.name ?? '')) {
        return { label: '', badge: round.abbreviation ?? round.name ?? null };
    }

    return { label: round.name ?? '', badge: null };
};

// A round's sticky header. Must sit inside its own group wrapper or every header
// pins at once; `bg-surface`, `z-10`, the `before:` overrides and `isPinned` stand
// in for the DS scrim, whose own offset declaration is invalid CSS.
export const RoundHeader = ({
    round,
    isStacked = false,
    isPinned = false,
    isFinalsMode = false
}) => {
    const { label, badge } = getRoundLabel(round);

    // Stacked headers sit above the cards, so there's room for the full name.
    const stackedText = label
        ? [label, badge].filter((part) => part !== null).join(' ')
        : round.name;

    const stackedClasses = isStacked ? 'py-3xs left-m!' : 'min-h-2xl p-xs';
    const scrimClasses = isPinned
        ? '[--scroll-area-header-bg:var(--color-surface)] [--scroll-area-header-gap:var(--spacing-xs)]'
        : '';

    return (
        <div
            className={`fixture-bar__round scroll-area-sticky-header z-10 flex shrink-0 items-center gap-2xs bg-surface before:pointer-events-none before:left-0 before:h-[calc(100%+var(--spacing-m))] ${stackedClasses} ${scrimClasses}`}
        >
            {isStacked ? (
                <span className="fixture-bar__round-name body-s-semi-bold whitespace-nowrap text-on-surface">
                    {stackedText}
                </span>
            ) : (
                <>
                    {label && (
                        <span className="fixture-bar__round-name label-m-semi-bold whitespace-nowrap text-on-surface">
                            {label}
                        </span>
                    )}
                    {badge !== null && (
                        <Badge
                            className="fixture-bar__round-number"
                            variant={BADGE_VARIANTS.VARIANT.PRIMARY}
                            size={BADGE_VARIANTS.SIZE.SMALL}
                            data-theme={isFinalsMode ? 'finals' : null}
                        >
                            {badge}
                        </Badge>
                    )}
                </>
            )}
        </div>
    );
};

RoundHeader.propTypes = {
    round: PropTypes.shape({
        name: PropTypes.string,
        abbreviation: PropTypes.string,
        roundNumber: PropTypes.number
    }).isRequired,
    isStacked: PropTypes.bool,
    isPinned: PropTypes.bool,
    isFinalsMode: PropTypes.bool
};
